import { createContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { toastEmitter } from "./toast-emitter.js";
import { ToastItem } from "./toast-item.js";
import type { Toast, ToastContextType, ToastType } from "./toast.types.js";

export const ToastContext = createContext<ToastContextType | undefined>(undefined); // eslint-disable-line react-refresh/only-export-components

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (type: ToastType, message: string, title?: string, duration: number = 4000) => {
      const id = Math.random().toString(36).substring(2, 9);
      const toast: Toast = { id, type, message, title, duration };

      setToasts((prev) => [...prev, toast]);

      if (duration > 0) {
        setTimeout(() => {
          removeToast(id);
        }, duration);
      }
    },
    [removeToast]
  );

  // Registering showToast globally so it can be called outside React =>(ex: QueryCache)
  useEffect(() => {
    toastEmitter.register(showToast);
    return () => toastEmitter.unregister();
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ toasts, showToast, removeToast }}>
      {children}
      <div className="pointer-events-none fixed top-4 right-4 z-[9999] flex w-full max-w-md flex-col gap-2">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};
